import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsApi } from '@/api/client';
import {
  buildApplyAction,
  buildSearchTermApplyAction,
} from '@/api/keywordRecommendationApply';
import {
  dismissRecommendations,
  filterDismissedRows,
  keywordRecommendationFingerprint,
  loadDismissedRecommendations,
  searchTermRecommendationFingerprint,
} from '@/api/recommendationDismiss';
import { keywordRowKey, searchTermRowKey } from '@/api/recommendationFormat';
import type {
  KeywordRecommendationItem,
  SearchTermRecommendationItem,
  SpKeywordApplyAction,
} from '@/types/keywordRecommendations';
import { useToast } from '@/components/ui/Toast';

type UseKeywordRecommendationActionsOptions = {
  profileId: string;
  campaignId?: string;
  apiKeywords: KeywordRecommendationItem[];
  apiSearchTerms: SearchTermRecommendationItem[];
};

export function useKeywordRecommendationActions({
  profileId,
  campaignId,
  apiKeywords,
  apiSearchTerms,
}: UseKeywordRecommendationActionsOptions) {
  const qc = useQueryClient();
  const { showToast } = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    loadDismissedRecommendations(profileId)
  );
  const [bidOverrides, setBidOverrides] = useState<Record<string, number>>({});
  const [pendingApply, setPendingApply] = useState<SpKeywordApplyAction[] | null>(null);
  const [pendingNegativeRow, setPendingNegativeRow] = useState<KeywordRecommendationItem | null>(
    null
  );

  const applyMutation = useMutation({
    mutationFn: async (actions: SpKeywordApplyAction[]) => {
      const negativeCreates = actions.filter((a) => a.action === 'ADD_NEGATIVE_KEYWORD');
      const otherActions = actions.filter((a) => a.action !== 'ADD_NEGATIVE_KEYWORD');

      const allResults: Array<{ status: string; message?: string }> = [];

      if (negativeCreates.length) {
        const res = await adsApi.createSpNegativeKeywords(profileId, {
          negative_keywords: negativeCreates.map((action) => ({
            campaign_id: String(action.campaignId || ''),
            ad_group_id: String(action.adGroupId || ''),
            keyword_text: String(action.keywordText || ''),
            match_type:
              (action.matchType as 'NEGATIVE_EXACT' | 'NEGATIVE_PHRASE') || 'NEGATIVE_EXACT',
            state: 'ENABLED',
          })),
        });
        const parsed = (res.data as { results?: Array<{ status: string; message?: string }> })
          ?.results;
        if (parsed?.length) allResults.push(...parsed);
      }

      if (otherActions.length) {
        const res = await adsApi.applySpKeywords(profileId, { actions: otherActions });
        const parsed = (res.data as { results?: Array<{ status: string; message?: string }> })
          ?.results;
        if (parsed?.length) allResults.push(...parsed);
      }

      return { results: allResults };
    },
    onSuccess: (payload) => {
      const results = payload?.results ?? [];
      const ok = results.filter((r) => r.status === 'success').length;
      const fail = results.filter((r) => r.status === 'error').length;
      showToast(
        `Applied ${ok} action${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`,
        fail && !ok ? 'error' : 'success'
      );
      setSelected(new Set());
      setPendingApply(null);
      setPendingNegativeRow(null);
      void qc.invalidateQueries({ queryKey: ['keyword-recommendations', profileId] });
      if (campaignId) {
        void qc.invalidateQueries({
          queryKey: ['campaign-keywords-catalog', profileId, campaignId],
        });
        void qc.invalidateQueries({
          queryKey: ['campaign-negative-keywords', profileId, campaignId],
        });
      }
    },
    onError: () => {
      showToast('Failed to apply keyword changes.', 'error');
      setPendingApply(null);
      setPendingNegativeRow(null);
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((keys: string[]) => {
    setSelected(new Set(keys));
  }, []);

  const handleBidChange = useCallback((key: string, bid: number) => {
    setBidOverrides((prev) => ({ ...prev, [key]: bid }));
  }, []);

  const handleDismiss = useCallback(
    (fingerprints: string[]) => {
      const next = dismissRecommendations(profileId, fingerprints);
      setDismissed(next);
      setSelected((prev) => {
        const updated = new Set(prev);
        for (const fingerprint of fingerprints) {
          for (const row of apiKeywords) {
            if (keywordRecommendationFingerprint(row.keywordId) === fingerprint) {
              updated.delete(keywordRowKey(row.keywordId));
            }
          }
          for (const row of apiSearchTerms) {
            if (
              searchTermRecommendationFingerprint(
                row.campaignId,
                row.adGroupId,
                row.searchTerm
              ) === fingerprint
            ) {
              updated.delete(searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm));
            }
          }
        }
        return updated;
      });
      showToast(
        fingerprints.length === 1
          ? 'Recommendation dismissed.'
          : `${fingerprints.length} recommendations dismissed.`,
        'success'
      );
    },
    [profileId, apiKeywords, apiSearchTerms, showToast]
  );

  const visibleKeywords = useMemo(
    () =>
      filterDismissedRows(apiKeywords, dismissed, (row) =>
        keywordRecommendationFingerprint(row.keywordId)
      ),
    [apiKeywords, dismissed]
  );

  const visibleSearchTerms = useMemo(
    () =>
      filterDismissedRows(apiSearchTerms, dismissed, (row) =>
        searchTermRecommendationFingerprint(row.campaignId, row.adGroupId, row.searchTerm)
      ),
    [apiSearchTerms, dismissed]
  );

  const collectSelectedActions = useCallback((): SpKeywordApplyAction[] => {
    const actions: SpKeywordApplyAction[] = [];
    for (const row of visibleKeywords) {
      const key = keywordRowKey(row.keywordId);
      if (!selected.has(key)) continue;
      const action = buildApplyAction(row, bidOverrides);
      if (action) actions.push(action);
    }
    for (const row of visibleSearchTerms) {
      const key = searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm);
      if (!selected.has(key)) continue;
      const action = buildSearchTermApplyAction(row, bidOverrides);
      if (action) actions.push(action);
    }
    return actions;
  }, [visibleKeywords, visibleSearchTerms, selected, bidOverrides]);

  const collectSelectedDismissFingerprints = useCallback((): string[] => {
    const fingerprints: string[] = [];
    for (const row of visibleKeywords) {
      const key = keywordRowKey(row.keywordId);
      if (!selected.has(key)) continue;
      fingerprints.push(keywordRecommendationFingerprint(row.keywordId));
    }
    for (const row of visibleSearchTerms) {
      const key = searchTermRowKey(row.campaignId, row.adGroupId, row.searchTerm);
      if (!selected.has(key)) continue;
      fingerprints.push(
        searchTermRecommendationFingerprint(row.campaignId, row.adGroupId, row.searchTerm)
      );
    }
    return fingerprints;
  }, [visibleKeywords, visibleSearchTerms, selected]);

  const handleBulkApply = useCallback(() => {
    const actions = collectSelectedActions();
    if (!actions.length) return;
    setPendingApply(actions);
  }, [collectSelectedActions]);

  const handleBulkDismiss = useCallback(() => {
    const fingerprints = collectSelectedDismissFingerprints();
    if (!fingerprints.length) return;
    handleDismiss(fingerprints);
  }, [collectSelectedDismissFingerprints, handleDismiss]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  return {
    selected,
    bidOverrides,
    dismissed,
    keywords: visibleKeywords,
    searchTerms: visibleSearchTerms,
    pendingApply,
    pendingNegativeRow,
    applying: applyMutation.isPending,
    applyMutation,
    toggleSelect,
    handleSelectAll,
    handleBidChange,
    handleDismiss,
    handleBulkApply,
    handleBulkDismiss,
    clearSelection,
    setPendingApply,
    setPendingNegativeRow,
  };
}
